import { useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button, type ButtonProps } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"

type ShotVideoResolutionPopoverProps = {
  value: string
  inherited: string
  hasOverride: boolean
  options: readonly string[]
  onChange: (override: string | null) => void
  disabled?: boolean
  triggerVariant?: ButtonProps["variant"]
  triggerClassName?: string
}

export function ShotVideoResolutionPopover({
  value,
  inherited,
  hasOverride,
  options,
  onChange,
  disabled = false,
  triggerVariant = "default",
  triggerClassName,
}: ShotVideoResolutionPopoverProps) {
  const { t } = useTranslation(["shot", "workspace"])
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "11rem" }),
    onOutsideClick: () => setOpen(false),
    onEscape: () => setOpen(false),
  })

  const choices = useMemo(
    () => [
      { value: "", label: t("workspace:providers.defaultFromStudio") },
      ...options.map((option) => ({ value: option, label: option })),
    ],
    [options, t],
  )

  if (options.length === 0) return null

  const selected = hasOverride ? value : ""

  const handleSelect = (next: string) => {
    const clears = next === "" || next === inherited
    onChange(clears ? null : next)
    setOpen(false)
  }

  const menu =
    open && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className="rounded-lg border border-ui-border-strong p-2.5 shadow-xl shadow-black/50 backdrop-blur-md dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
      >
        <p className="mb-1.5 text-[10px] font-medium tracking-wide text-ui-text-secondary uppercase">
          {t("panel.videoResolution")}
        </p>
        <div className="flex flex-col gap-1">
          {choices.map((choice) => (
            <button
              key={choice.value || "inherit"}
              type="button"
              className={cn(
                "rounded-md px-2 py-1 text-left text-[11px] font-medium transition-colors",
                choice.value === selected
                  ? UI_ACCENT_FILLED
                  : "bg-ui-surface-raised text-ui-text-secondary hover:text-ui-text-primary",
              )}
              onClick={() => handleSelect(choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </div>
      </div>
    ) : null

  return (
    <div ref={rootRef} className="relative">
      <Button
        ref={buttonRef}
        type="button"
        variant={triggerVariant}
        size="sm"
        disabled={disabled}
        className={cn("tabular-nums", triggerClassName)}
        title={t("panel.changeVideoResolution")}
        onClick={() => setOpen((current) => !current)}
      >
        {value || "—"}
      </Button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
